import { useMutation } from '@apollo/client';
import { compatibility } from '@seek/ca-myprofile-utils';
import { useLanguage, useTranslations } from '@vocab/react';
import { TextLink } from 'braid-design-system';
import { useEffect } from 'react';

import { PageTitles } from 'src/components/Notifications/pageTitle';
import { browserLogError } from 'src/modules/error-logger/browserLog';
import { unsubscribeApacSavedSearchByCode } from 'src/modules/graphql/notifications/saved-search.graphql';
import { useTypedSelector } from 'src/reducers';
import type {
  UnsubscribeApacSavedSearchByCodeMutation,
  UnsubscribeApacSavedSearchByCodeMutationVariables,
} from 'src/types/graphql';

import Subscription from '../common/subscription';
import PageLoader from '../page-loader';
import { useRoutes } from '../routes';
import {
  trackUnsubscribeAllSuccessPageLoad,
  trackUnsubscribeSuccessPageLoad,
} from '../tracking/tealium';

import translations from './.vocab';
import { UnsubscribeIcon } from './svg/unsubscribe-icon';
import type { Action } from './types';
import UnsubscribeError from './unsubscribe-error';

const UnsubscribeJobAlert = (props: {
  token: string;
  authenticationStatus: string;
  action?: Action;
}) => {
  const { t } = useTranslations(translations);

  const { language: vocabLanguage } = useLanguage();
  const language =
    compatibility.getCompatibleLanguageFromLocaleString(vocabLanguage);

  const { homePath, savedSearchesPath } = useRoutes();
  const { token: originalToken, authenticationStatus, action } = props;

  const isUnsubscribeAll = action === 'unsubscribe-all';
  const token = isUnsubscribeAll ? `all-${originalToken}` : originalToken;

  const zone = useTypedSelector((store) => store.page.pageAnalytics.zone);
  const [unsubscribeJobAlert, { data, error }] = useMutation<
    UnsubscribeApacSavedSearchByCodeMutation,
    UnsubscribeApacSavedSearchByCodeMutationVariables
  >(unsubscribeApacSavedSearchByCode);

  const authenticated = authenticationStatus === 'authenticated';
  const savedSearchPayload = data?.unsubscribeApacSavedSearchByCode;
  const isFailurePayload = savedSearchPayload && 'error' in savedSearchPayload;
  const brand =
    PageTitles[useTypedSelector((state) => state.page.pageAnalytics.brand)] ??
    PageTitles.seek;

  const getBodyContent = () => {
    if (isUnsubscribeAll) {
      return authenticated
        ? t('saved-search-signed-in-unsubscribe-all-message', {
            brand,
          })
        : t('saved-search-signed-out-unsubscribe-all-message', {
            TextLink: (children) => (
              <TextLink data-testid="sign-in-link" href={savedSearchesPath}>
                {children}
              </TextLink>
            ),
            brand,
          });
    }

    return authenticated
      ? t('default-saved-search-signed-in-message')
      : t('default-saved-search-signed-out-message');
  };

  useEffect(() => {
    if (token) {
      unsubscribeJobAlert({ variables: { input: { code: token } } }).catch(
        (e) => {
          browserLogError(e);
        },
      );
    }
  }, [unsubscribeJobAlert, token]);

  if (error || isFailurePayload) {
    return <UnsubscribeError language={language} zone={zone} action={action} />;
  }

  if (savedSearchPayload && !isFailurePayload) {
    if (isUnsubscribeAll) {
      trackUnsubscribeAllSuccessPageLoad('jobAlert', 'email');
    } else {
      trackUnsubscribeSuccessPageLoad('jobAlert', 'email');
    }

    return (
      <Subscription
        title={
          isUnsubscribeAll
            ? t('saved-search-heading-unsubscribe-all')
            : t('default-saved-search-heading')
        }
        cardTitle={authenticated ? t('ja-subscription-card-title') : undefined}
        body={getBodyContent()}
        svg={<UnsubscribeIcon />}
        link={authenticated ? savedSearchesPath : homePath}
        linkMessage={
          authenticated ? t('default-saved-search-link-message') : undefined
        }
        linkMessage2={
          authenticated ? t('unsubscribe-subscription-link-message') : undefined
        }
      />
    );
  }

  return <PageLoader />;
};

export default UnsubscribeJobAlert;
